/**
 * AICopilotComposerTools.jsx
 *
 * Translate + Rewrite tools that act on whatever text is currently in
 * the message composer. Used directly above the textarea in InboxPage.
 *
 * - Translate: pick a target language, call /ai-copilot/translate,
 *   replace the composer contents with the translation.
 * - Rewrite: pick a style, call /ai-copilot/rewrite, replace contents.
 *
 * Both tools require `enabled && (translation_enabled | rewrite_enabled)`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import {
  Languages, Wand2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LANGS = [
  ['en', 'English'], ['hi', 'Hindi'], ['ar', 'Arabic'], ['es', 'Spanish'],
  ['fr', 'French'], ['de', 'German'], ['pt', 'Portuguese'], ['ja', 'Japanese'],
  ['zh', 'Chinese'], ['tr', 'Turkish'],
];

const STYLES = [
  ['shorter',     'Shorter'],
  ['friendlier',  'Friendlier'],
  ['formal',      'More formal'],
  ['fix_grammar', 'Fix grammar'],
  ['longer',      'Longer'],
  ['persuasive',  'More persuasive'],
];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AICopilotComposerTools({ text, onReplace }) {
  const [translateOn, setTranslateOn] = useState(false);
  const [rewriteOn, setRewriteOn] = useState(false);
  const [known, setKnown] = useState(false);
  const [busy, setBusy] = useState('');
  const [primaryLang, setPrimaryLang] = useState('en');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/ai-copilot/settings`, { headers: authHeaders() });
        if (cancelled) return;
        const s = res.data || {};
        setTranslateOn(!!(s.enabled && s.translation_enabled));
        setRewriteOn(!!(s.enabled && s.rewrite_enabled));
        setPrimaryLang(s.primary_language || 'en');
      } catch {
        setTranslateOn(false); setRewriteOn(false);
      } finally {
        if (!cancelled) setKnown(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const callTranslate = useCallback(async (target) => {
    if (!text || !text.trim()) {
      toast.info('Type something in the composer first');
      return;
    }
    setBusy('translate');
    try {
      const res = await axios.post(`${API_BASE}/ai-copilot/translate`,
        { text, target_language: target },
        { headers: authHeaders() });
      if (res.data?.translated) {
        onReplace && onReplace(res.data.translated);
        toast.success(`Translated to ${target.toUpperCase()}`);
      } else {
        toast.error('No translation returned');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translate failed');
    } finally {
      setBusy('');
    }
  }, [text, onReplace]);

  const callRewrite = useCallback(async (style) => {
    if (!text || !text.trim()) {
      toast.info('Type something in the composer first');
      return;
    }
    setBusy('rewrite');
    try {
      const res = await axios.post(`${API_BASE}/ai-copilot/rewrite`,
        { text, style },
        { headers: authHeaders() });
      if (res.data?.rewritten) {
        onReplace && onReplace(res.data.rewritten);
        toast.success(`Rewritten — ${style.replaceAll('_', ' ')}`);
      } else {
        toast.error('No rewrite returned');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rewrite failed');
    } finally {
      setBusy('');
    }
  }, [text, onReplace]);

  if (!known) return null;
  if (!translateOn && !rewriteOn) return null;

  return (
    <div className="contents" data-testid="ai-composer-tools">
      {/* `display:contents` so each button is a direct child of the
          parent toolbar's flex-wrap container — no extra wrapper takes
          a row of its own. */}
      {translateOn && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm"
                    className="h-6 px-1.5 text-[10px] gap-1 text-wt-text-muted hover:text-wt-text-primary"
                    disabled={busy === 'translate'}
                    title="Translate composer text"
                    data-testid="ai-translate-trigger">
              {busy === 'translate'
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Languages className="h-3 w-3" />}
              <span className="hidden sm:inline">Translate</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" align="start" side="top" data-testid="ai-translate-popover">
            <div className="text-[10px] uppercase tracking-wide text-wt-text-muted px-2 py-1">Target language</div>
            {LANGS.map(([v, l]) => (
              <button key={v}
                      onClick={() => callTranslate(v)}
                      className={`w-full text-left text-xs px-2 py-1 rounded hover:bg-wt-paper ${v === primaryLang ? 'font-semibold text-wt-text-primary' : 'text-wt-text-primary/80'}`}
                      data-testid={`ai-translate-${v}`}>
                {l}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      {rewriteOn && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm"
                    className="h-6 px-1.5 text-[10px] gap-1 text-wt-text-muted hover:text-wt-text-primary"
                    disabled={busy === 'rewrite'}
                    title="Rewrite composer text"
                    data-testid="ai-rewrite-trigger">
              {busy === 'rewrite'
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Wand2 className="h-3 w-3" />}
              <span className="hidden sm:inline">Rewrite</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" align="start" side="top" data-testid="ai-rewrite-popover">
            <div className="text-[10px] uppercase tracking-wide text-wt-text-muted px-2 py-1">Style</div>
            {STYLES.map(([v, l]) => (
              <button key={v}
                      onClick={() => callRewrite(v)}
                      className="w-full text-left text-xs px-2 py-1 rounded hover:bg-wt-paper text-wt-text-primary/80"
                      data-testid={`ai-rewrite-${v}`}>
                {l}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
